import type { Auth, SignInExtra } from './auth';
import { withBase } from './base-path';

/**
 * Login próprio da plataforma (sem redirecionar ao Keycloak): e-mail e senha, código do
 * autenticador e links de convite/redefinição vão ao núcleo; o refresh fica num cookie HttpOnly
 * só das rotas de login e o token de acesso (5 min) só na memória da aba.
 */
export interface AuthAnswer {
  /** 'probe': volta de um teste de login em nova aba (R01-11) — não abre sessão; o resultado vai para a aba da configuração. */
  /** 'step-up' (R01-17 B): ir ao provedor (url) pedir uma autenticação mais forte e voltar. */
  status: 'ok' | 'mfa' | 'enroll' | 'probe' | 'step-up';
  url?: string;
  challenge?: string;
  accessToken?: string;
  expiresIn?: number;
  otpauthUri?: string;
  secret?: string;
  recoveryCodes?: string[];
}

export interface TenantAuth extends Auth {
  login(email: string, password: string): Promise<AuthAnswer>;
  mfa(challenge: string, code: string): Promise<AuthAnswer>;
  enroll(challenge: string, code: string): Promise<AuthAnswer>;
  inviteInfo(token: string): Promise<{ email: string; name: string }>;
  acceptInvite(token: string, password: string): Promise<AuthAnswer>;
  requestReset(email: string): Promise<void>;
  resetInfo(token: string): Promise<{ email: string; name: string }>;
  reset(token: string, password: string): Promise<AuthAnswer>;
  /** Volta do provedor da empresa: entra, ou devolve o próximo passo (código/cadastro do autenticador). */
  ssoCallback(): Promise<AuthAnswer>;
  /** Para onde voltar depois de entrar (guardado antes de sair para o provedor ou da tela de entrada). */
  returnTo(): string;
}

export class AuthError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Record<string, unknown>,
  ) {
    super(String(problem.title ?? problem.detail ?? `HTTP ${status}`));
  }
  get code(): string | undefined {
    return this.problem.code as string | undefined;
  }
}

const RETURN_KEY = 'hive.returnTo';

/** Guarda para onde voltar depois de ir ao provedor (step-up de uma ação sensível, R01-17 B). */
export function rememberReturn(path: string) {
  try {
    sessionStorage.setItem(RETURN_KEY, safe(path));
  } catch {
    /* sem armazenamento */
  }
}

function safe(path: string | null | undefined): string {
  return path && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/auth/') ? path : '/';
}

export function createTenantAuth(slug: string, baseUrl = ''): TenantAuth {
  return createPasswordAuth(`${baseUrl}/api/v1/public/auth/${encodeURIComponent(slug)}`);
}

/** Equipe do provedor: o mesmo login próprio, nas rotas da console do provedor. */
export function createProviderAuth(baseUrl = ''): TenantAuth {
  return createPasswordAuth(`${baseUrl}/provider-api/v1/public/auth`);
}

export function createPasswordAuth(base: string): TenantAuth {
  let token: string | null = null;
  let until = 0;
  let renewing: Promise<string | null> | null = null;

  async function post<T>(path: string, body?: unknown): Promise<T> {
    const res = await fetch(base + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept-Language': document.documentElement.lang || navigator.language,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 204 || res.status === 202) return undefined as T;
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) throw new AuthError(res.status, data);
    return data as T;
  }

  function keep(a: AuthAnswer): AuthAnswer {
    if (a.status === 'ok' && a.accessToken) {
      token = a.accessToken;
      until = Date.now() + (a.expiresIn ?? 300) * 1000;
    }
    return a;
  }

  /**
   * Renova pelo cookie de refresh. Só a recusa do núcleo (401/400: sessão vencida ou revogada) encerra a sessão;
   * núcleo fora do ar (rede, 502/503/504 durante um reinício ou atualização) tenta de novo por até 90 s.
   */
  async function refresh(): Promise<string | null> {
    renewing ??= (async () => {
      const deadline = Date.now() + 90_000;
      for (let wait = 1000; ; wait = Math.min(wait * 2, 8000)) {
        try {
          // abas do mesmo navegador renovam uma de cada vez (cada uma com o cookie que a anterior acabou de girar)
          const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
          keep(locks ? await locks.request('hive-refresh', () => post<AuthAnswer>('/refresh')) : await post<AuthAnswer>('/refresh'));
          return token;
        } catch (e) {
          const status = e instanceof AuthError ? e.status : 0;
          const down = status === 0 || status >= 500;
          if (!down || Date.now() + wait > deadline) {
            token = null;
            return null;
          }
          await new Promise((r) => setTimeout(r, wait));
        }
      }
    })().finally(() => {
      renewing = null;
    });
    return renewing;
  }

  return {
    login: (email, password) => post<AuthAnswer>('/login', { email, password }).then(keep),
    mfa: (challenge, code) => post<AuthAnswer>('/mfa', { challenge, code }).then(keep),
    enroll: (challenge, code) => post<AuthAnswer>('/enroll', { challenge, code }).then(keep),
    inviteInfo: (t) => post('/invite/info', { token: t }),
    acceptInvite: (t, password) => post<AuthAnswer>('/invite/accept', { token: t, password }).then(keep),
    requestReset: (email) => post('/reset/request', { email }),
    resetInfo: (t) => post('/reset/info', { token: t }),
    reset: (t, password) => post<AuthAnswer>('/reset', { token: t, password }).then(keep),
    returnTo() {
      const r = sessionStorage.getItem(RETURN_KEY);
      sessionStorage.removeItem(RETURN_KEY);
      return safe(r);
    },

    /** Entrar de novo: com a conta da empresa vai ao provedor; senão volta para a tela de entrada. */
    async signIn(returnTo: string, extra?: SignInExtra) {
      sessionStorage.setItem(RETURN_KEY, safe(returnTo));
      if (extra?.idpHint === 'oidc' || extra?.idpHint === 'saml') {
        const r = await post<{ url: string }>(extra.idpHint === 'saml' ? '/saml/start' : '/oidc/start', { loginHint: extra.loginHint ?? null });
        window.location.assign(r.url);
        return;
      }
      token = null;
      await post('/logout').catch(() => undefined);
      window.location.assign(withBase('/'));
    },
    /** Volta do provedor da empresa (/auth/callback?code&state). */
    async ssoCallback() {
      const q = new URLSearchParams(window.location.search);
      const err = q.get('error_description') ?? q.get('error');
      if (err) throw new AuthError(400, { title: err, code: 'sso-error' });
      return keep(await post<AuthAnswer>('/oidc/callback', { code: q.get('code'), state: q.get('state') }));
    },
    async handleCallback() {
      const a = await this.ssoCallback();
      if (a.status !== 'ok') throw new AuthError(409, { title: a.status, code: `next-${a.status}` });
      return this.returnTo();
    },
    async getToken() {
      if (token && Date.now() < until - 30_000) return token;
      return refresh();
    },
    getUser: async () => null,
    async signOut() {
      token = null;
      // com "sair também no provedor" ligado, o núcleo devolve o endereço de fim de sessão do provedor (OIDC)
      const r = await post<{ providerLogoutUrl?: string } | undefined>('/logout').catch(() => undefined);
      const out = r?.providerLogoutUrl;
      window.location.assign(out && /^https?:\/\//.test(out) ? out : withBase('/'));
    },
    async forget() {
      token = null;
    },
    async renew() {
      token = null;
      return refresh();
    },
  };
}
